import { useState, type ReactElement } from "react";
import type { ParetoParams } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { Button } from "../../atoms/index.js";
import { DataTable } from "../../molecules/DataTable/index.js";
import { ParetoEditor } from "../expanded-editors.js";
import { ParetoBody, paretoSeries, type ParetoAccent } from "./Component.js";

function ParetoExpanded({
  data,
  onChange,
  readOnly,
  accent,
}: WidgetBodyProps<ParetoParams> & { accent: ParetoAccent }): ReactElement {
  const [editing, setEditing] = useState(false);
  const series = paretoSeries(data.bars);
  const total = data.bars.reduce((sum, bar) => sum + bar.value, 0);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      {!readOnly && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(!editing)}
          >
            {editing ? "Done editing" : "Edit data"}
          </Button>
        </div>
      )}
      {editing && !readOnly ? (
        <ParetoEditor
          data={data}
          onChange={(next) => onChange(next as ParetoParams)}
          accent={accent}
        />
      ) : (
        <div className="grid min-h-0 flex-1 gap-6 overflow-auto lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <div className="flex min-h-80 flex-col">
            <ParetoBody data={data} accent={accent} />
          </div>
          {/* The contribution grid is a DataTable: its frame, header, cell
              and footer paint replace the hand-built table's (look row
              OK-48). */}
          <div className="self-start">
            <DataTable
              columns={[
                {
                  id: "contributor",
                  header: "Contributor",
                  rowHeader: true,
                  cell: ({ bar }) => (
                    <span className="block max-w-xs break-words">
                      {bar.label}
                    </span>
                  ),
                  footer: "Total",
                },
                {
                  id: "value",
                  header: data.unit || "Value",
                  align: "end",
                  cell: ({ bar }) => bar.value.toLocaleString(),
                  footer: total.toLocaleString(),
                },
                {
                  id: "share",
                  header: "Share",
                  align: "end",
                  cell: ({ bar }) =>
                    total ? ((bar.value / total) * 100).toFixed(1) + "%" : "—",
                  footer: total ? "100%" : "—",
                },
                {
                  id: "cumulative",
                  header: "Cumulative",
                  align: "end",
                  cell: ({ bar }) => (total ? bar.cumulative + "%" : "—"),
                  footer: "—",
                },
              ]}
              data={series.map((bar, index) => ({ bar, id: String(index) }))}
              getRowId={(row) => row.id}
              caption={`Contributors · ${series.length}`}
              showCaption
              footerWhenEmpty
              // DS keep: the empty text is a start-aligned muted line with the
              // grid's 12px inset above the Total row; EmptyState is a centred
              // title block and has no inline, padded form.
              emptyState={
                <p className="p-3 text-sm text-pb-text-muted">
                  No contributors yet.
                </p>
              }
            />
            {total > 0 && (
              <p className="p-3 text-xs text-pb-text-muted">
                Shares may differ slightly from 100% due to rounding.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ParetoL1ExpandedEditor(
  props: WidgetBodyProps<ParetoParams>,
): ReactElement {
  return <ParetoExpanded {...props} accent="purple" />;
}
export function ParetoL2ExpandedEditor(
  props: WidgetBodyProps<ParetoParams>,
): ReactElement {
  return <ParetoExpanded {...props} accent="cyan" />;
}
